import React, {useEffect, useState} from "react";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Paper,
  TextField,
  Typography,
  Chip,
  Toolbar,
  Button,
  CircularProgress,
  Box,
  IconButton
} from "@material-ui/core";
import { TableSortLabel } from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import API from "../../../network/dashboardAPI";
import DelipointTierDialog from "./component/DelipointTierDialog";
import ActionButtons from "./component/DeleteDialog";
import useAlert from "../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc",
		minHeight: "100vh"
	},
	paper: {
		borderRadius: 12,
		overflow: "hidden",
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
	},
	toolbar: {
		display: "flex",
		justifyContent: "space-between",
		padding: theme.spacing(2)
	},
	searchBox: {
		display: "flex",
		alignItems: "center",
		padding: "2px 8px",
		borderRadius: 8,
		backgroundColor: "#fff",
		border: "1px solid #ddd",
		width: 300
	},
	searchInput: {
		marginLeft: theme.spacing(1),
		flex: 1
	},
	tableHead: {
		backgroundColor: "#f4f6f8",
		display: 'flex',
		fontWeight: 600,
		borderBottom: '1px solid #eee',
		padding: '8px 0',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		borderBottom: '1px solid #f0f0f0',
		transition: 'background 0.2s',
		minHeight: 48,
	},
	cell: {
		flex: 1,
		padding: '8px',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	dragHandle: {
		cursor: 'grab',
		marginRight: 8,
		userSelect: 'none',
	},
	statusChip: {
		fontWeight: "bold"
	},
	actionButton: {
		marginLeft: theme.spacing(1)
	},
	loadingBox: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3)
	},
	refreshButton: {
		marginLeft: theme.spacing(1)
	},
	actionSection: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1)
	}
}));

export default function DelipointTiersList() {
	const classes = useStyles();
	const alert = useAlert();

	const [data, setData] = useState([]);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [orderBy, setOrderBy] = useState("order");
	const [order, setOrder] = useState("asc");
	const [loading, setLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(null);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);

	const getRowId = (row) => row?._id || row?.point_id || row?.id;

	// Drag and drop handler
	const handleOnDragEnd = async (result) => {
		if (!result.destination) return;
		const items = Array.from(data);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);
		// Update order field based on new position (1-based)
		const updatedItems = items.map((item, idx) => ({ ...item, order: idx + 1 }));
		setData(updatedItems);

		// Persist new order to backend
		try {
			for (const item of updatedItems) {
				// Only send minimal fields needed for order update
				const id = item.dp_tier_id || item._id || item.id;
				const response = await API.updateDelipointTier({
					...item,
					order: item.order,
					delipointId: id
				});
				const res = await response.json();
				if (!response.ok || !res.success) {
					throw new Error(res.message || 'Could not update tier order');
				}
			}
			alert.success("Order Updated", "Delipoint tier order has been updated.");
			fetchDelipoints();
		} catch (err) {
			alert.error("Order Update Failed", "Could not update tier order. Please try again.");
		}
	};

	const fetchDelipoints = async () => {
		try {
			setLoading(true);
			const params = {
				search,
				page,
				limit: rowsPerPage,
				sortField: orderBy,
				sortOrder: order,
				_locale: "en"
			};

			const response = await API.getDelipointTiersList(params);
			const res = await response.json();

			if (res.success) {
				setData(res.data || []);
				setTotal(res.total || 0);
				if ((res.total || 0) === 0 && search) {
					alert.toast("No delipoint found for your search", "info");
				}
			} else {
				alert.error("Failed to Load", res.message || "Unable to fetch delipoint tiers");
			}
		} catch (err) {
			console.error("Error fetching delipoint:", err);
			alert.error(
				"Network Error",
				"Failed to load delipoint. Please check your connection."
			);
		} finally {
			setLoading(false);
		}
	};

	const handleSort = (field) => {
		if (orderBy === field) {
			setOrder(order === "asc" ? "desc" : "asc");
		} else {
			setOrderBy(field);
			setOrder("asc");
		}
		setPage(0);
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(0);
	};

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			fetchDelipoints();
		}
	};

	const handleAdd = () => {
		setSelectedItem(null);
		setDialogOpen(true);
	};

	const handleEdit = (item) => {
		// Ensure the item always has an id property for dialog usage
		const id = getRowId(item);
		const itemWithId = id && !item.id ? { ...item, id } : item;
		setSelectedItem(itemWithId);
		setDialogOpen(true);
	};

	const handleDelete = async (row) => {
		try {
			const itemId = getRowId(row);
			const result = await alert.confirm(
				"Delete Delipoint?",
				`Are you sure you want to delete "${row.name}"? This action cannot be undone.`,
				"Yes, Delete",
				"Cancel"
			);

			if (!result.isConfirmed || !itemId) return;

			setDeleteLoading(itemId);

			const response = await API.deleteDelipointTier(itemId);
			const res = await response.json();

			if (res.success) {
				alert.success("Deleted Successfully", `"${row.name}" has been deleted successfully.`);
				alert.toast("Delipoint deleted");
				fetchDelipoints();
			} else {
				alert.error("Delete Failed", res.message || "Failed to delete delipoint");
			}
		} catch (err) {
			console.error("Error deleting delipoint:", err);
			alert.error("Delete Failed", "Unable to delete delipoint. Please try again.");
		} finally {
			setDeleteLoading(null);
		}
	};

	// Refresh list after dialog save
	const handleDialogSaved = (action, itemName) => {
		fetchDelipoints();
		if (action === "add") {
			alert.success("Delipoint Added", `"${itemName}" has been created successfully.`);
			alert.toast("New delipoint added");
		} else if (action === "edit") {
			alert.success("Delipoint Updated", `"${itemName}" has been updated successfully.`);
			alert.toast("Delipoint updated");
		}
	};

	// Debounced search effect
	useEffect(() => {
		const timer = setTimeout(() => {
			fetchDelipoints();
		}, 500); // 500ms debounce

		return () => clearTimeout(timer);
	}, [search, page, rowsPerPage, orderBy, order]);

	// Initial load
	useEffect(() => {
		fetchDelipoints();
	}, []);

	return (
		<div className={classes.root}>
			<Typography variant="h5" gutterBottom style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
				Delipoint Tiers
				{loading && <CircularProgress size={20}/>} 
			</Typography>

			<Paper className={classes.paper}>
				<Toolbar className={classes.toolbar}>
					<div className={classes.searchBox}>
						<SearchIcon color="action"/>
						<TextField
							placeholder="Search delipoint..."
							value={search}
							onChange={handleSearch}
							onKeyPress={handleSearchSubmit}
							className={classes.searchInput}
							InputProps={{disableUnderline: true}}
						/>
					</div>
					<div className={classes.actionSection}>
						{/* <IconButton
							onClick={handleRefresh}
							disabled={loading}
							className={classes.refreshButton}
							title="Refresh"
						>
							<RefreshIcon/>
						</IconButton> */}
						<Button
							variant="contained"
							color="primary"
							onClick={handleAdd}
							disabled={loading}
						>
							Add New
						</Button>
					</div>
				</Toolbar>


				<div style={{ width: '100%' }}>
					<div className={classes.tableHead}>
						<div className={classes.cell} style={{ flex: 0.5 }}>
							<TableSortLabel
								active={orderBy === "order"}
								direction={orderBy === "order" ? order : "asc"}
								onClick={() => handleSort("order")}
							>
								Order
							</TableSortLabel>
						</div>
						<div className={classes.cell} style={{ flex: 1 }}>
							<TableSortLabel
								active={orderBy === "_id"}
								direction={orderBy === "_id" ? order : "asc"}
								onClick={() => handleSort("_id")}
							>
								Tier ID
							</TableSortLabel>
						</div>
						<div className={classes.cell} style={{ flex: 1 }}>Image</div>
						<div className={classes.cell} style={{ flex: 2 }}>
							<TableSortLabel
								active={orderBy === "name"}
								direction={orderBy === "name" ? order : "asc"}
								onClick={() => handleSort("name")}
							>
								Name
							</TableSortLabel>
						</div>
						<div className={classes.cell} style={{ flex: 1 }}>Tier Order Value</div>
						<div className={classes.cell} style={{ flex: 1 }}>Multiplier</div>
						<div className={classes.cell} style={{ flex: 1 }}>Status</div>
						<div className={classes.cell} style={{ flex: 1, textAlign: 'right' }}>Actions</div>
					</div>
					<DragDropContext onDragEnd={handleOnDragEnd}>
						<Droppable droppableId="delipoints-tiers">
							{(provided) => (
								<div
									ref={provided.innerRef}
									{...provided.droppableProps}
									style={{ minHeight: 48, width: '100%' }}
								>
									{loading && data.length === 0 ? (
										<div className={classes.row}>
											<div className={classes.cell} style={{ flex: 8 }}>
												<Box className={classes.loadingBox}>
													<CircularProgress/>
													<Typography variant="body2" style={{marginLeft: 16}}>
														Loading delipoint...
													</Typography>
												</Box>
											</div>
										</div>
									) : data.length === 0 ? (
										<div className={classes.row}>
											<div className={classes.cell} style={{ flex: 8, textAlign: 'center', padding: 40 }}>
												<Typography variant="h6" color="textSecondary" gutterBottom>
													No delipoint found
												</Typography>
												{search && (
													<Typography variant="body2" color="textSecondary">
														Try adjusting your search terms
													</Typography>
												)}
											</div>
										</div>
									) : (
										data.map((row, idx) => {
											// Always use a unique string for draggableId
											const itemId = String(getRowId(row));
											return (
												<Draggable key={itemId} draggableId={itemId} index={idx}>
													{(provided, snapshot) => (
														<div
															className={classes.row}
															ref={provided.innerRef}
															{...provided.draggableProps}
															style={{
																background: snapshot.isDragging ? '#f8f9fa' : undefined,
																...provided.draggableProps.style,
															}}
														>
															<div className={classes.cell} style={{ flex: 0.5 }} {...provided.dragHandleProps}>
																<span className={classes.dragHandle}>☰</span>{row.order}
															</div>
															<div className={classes.cell} style={{ flex: 1 }}>{row._id}</div>
															<div className={classes.cell} style={{ flex: 1 }}>
																{row.image ? (
																	<img
																		src={row.image}
																		alt={row.name}
																		style={{width: 40, height: 40, borderRadius: 8, objectFit: "cover"}}
																		onError={(e) => {
																			e.target.style.display = "none";
																			e.target.nextSibling.style.display = "inline";
																		}}
																	/>
																) : null}
																<span style={{display: row.image ? "none" : "inline"}}>
																	No Image
																</span>
															</div>
															<div className={classes.cell} style={{ flex: 2 }}>
																<Typography variant="body2" fontWeight="medium">
																	{row.name}
																</Typography>
																{row.name_kh && (
																	<Typography variant="caption" color="textSecondary">
																		{row.name_kh}
																	</Typography>
																)}
															</div>
															<div className={classes.cell} style={{ flex: 1 }}>{row.tier_order_value}</div>
															<div className={classes.cell} style={{ flex: 1 }}>{row.multiplier}</div>
															<div className={classes.cell} style={{ flex: 1 }}>
																<Chip
																	label={row.is_deleted ? "Deleted" : "Active"}
																	color={row.is_deleted ? "secondary" : "primary"}
																	size="small"
																	className={classes.statusChip}
																/>
															</div>
															<div className={classes.cell} style={{ flex: 1, textAlign: 'right' }}>
																<ActionButtons
																	row={row}
																	handleEdit={handleEdit}
																	handleDelete={handleDelete}
																	deleteLoading={deleteLoading === getRowId(row)}
																/>
															</div>
														</div>
													)}
												</Draggable>
											);
										})
									)}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>

				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0' }}>
					<div>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setPage(Math.max(0, page - 1))}
							disabled={page === 0}
							style={{ marginRight: 8 }}
						>
							Previous
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setPage(page + 1)}
							disabled={(page + 1) * rowsPerPage >= total}
						>
							Next
						</Button>
						<span style={{ marginLeft: 16 }}>
							Page {page + 1} of {Math.max(1, Math.ceil(total / rowsPerPage))}
						</span>
					</div>
					<div>
						<span>Rows per page: </span>
						<select
							value={rowsPerPage}
							onChange={e => {
								setRowsPerPage(Number(e.target.value));
								setPage(0);
							}}
							style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
						>
							{[5, 10, 25, 50].map(opt => (
								<option key={opt} value={opt}>{opt}</option>
							))}
						</select>
					</div>
				</div>
			</Paper>

			{/* Add / Edit Dialog */}
			<DelipointTierDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onSaved={handleDialogSaved}
				initialData={selectedItem}
			/>
		</div>
	);
}